import * as THREE from 'three';
import type { BoxCollider } from './types';
import type { GrainSpawnRecord } from './spawn';
import { grainRowSpans } from './spawn';
import { createWindowRingAllocator, slotRuns, type WindowRingAllocator } from './windowRing';
import {
  posFragSource,
  velFragSource,
  RENDER_FRAG,
  RENDER_VERT,
  SIM_VERT,
} from './shaders';
import { makeFloatTarget, uploadSpans } from './gpuUtils';
import type { ArenaXZ } from './types';

export interface GrainSim {
  readonly capacity: number;
  readonly texSize: number;
  readonly mesh: THREE.InstancedMesh;
  readonly posTexture: () => THREE.Texture;
  readonly velTexture: () => THREE.Texture;
  readonly metaTexture: () => THREE.Texture;
  readonly originTexture: () => THREE.Texture;
  enqueue(records: GrainSpawnRecord[], simTime?: number): number;
  pendingCount(): number;
  unexpiredCount(now: number): number;
  step(
    renderer: THREE.WebGLRenderer,
    dt: number,
    heightTex: THREE.Texture,
    baseTex?: THREE.Texture | null,
    liveCount?: number,
  ): void;
  reset(renderer: THREE.WebGLRenderer): void;
  setColliders(boxes: BoxCollider[]): void;
  dispose(): void;
}

function writeDead(target: THREE.WebGLRenderTarget, size: number, renderer: THREE.WebGLRenderer): void {
  const data = new Float32Array(size * size * 4);
  for (let i = 0; i < size * size; i += 1) {
    data[i * 4 + 1] = -100;
    data[i * 4 + 3] = -1;
  }
  const prev = renderer.getRenderTarget();
  renderer.setRenderTarget(target);
  const gl = renderer.getContext() as WebGL2RenderingContext;
  const props = renderer.properties.get(target.texture) as { __webglTexture?: WebGLTexture } | undefined;
  const tex = props?.__webglTexture ?? null;
  if (tex) {
    const prevTex = gl.getParameter(gl.TEXTURE_BINDING_2D);
    gl.bindTexture(gl.TEXTURE_2D, tex);
    gl.texSubImage2D(gl.TEXTURE_2D, 0, 0, 0, size, size, gl.RGBA, gl.FLOAT, data);
    gl.bindTexture(gl.TEXTURE_2D, prevTex);
  }
  renderer.setRenderTarget(prev);
}

function fillBoxUniforms(
  centers: THREE.Vector3[],
  halves: THREE.Vector3[],
  boxes: BoxCollider[],
): number {
  const n = Math.min(16, boxes.length);
  for (let i = 0; i < 16; i += 1) {
    if (i < n) {
      const b = boxes[i];
      centers[i].set(b.center[0], b.center[1], b.center[2]);
      halves[i].set(b.half[0], b.half[1], b.half[2]);
    } else {
      centers[i].set(0, -100, 0);
      halves[i].set(0, 0, 0);
    }
  }
  return n;
}

export function createGrainSim(
  renderer: THREE.WebGLRenderer,
  capacity: number,
  arena: ArenaXZ,
  boxes: BoxCollider[],
  opts?: { spriteScale?: number; spriteHard?: number },
): GrainSim {
  const texSize = Math.max(1, Math.ceil(Math.sqrt(capacity)));
  const posA = makeFloatTarget(texSize, THREE.NearestFilter);
  const posB = makeFloatTarget(texSize, THREE.NearestFilter);
  const velA = makeFloatTarget(texSize, THREE.NearestFilter);
  const velB = makeFloatTarget(texSize, THREE.NearestFilter);
  const meta = makeFloatTarget(texSize, THREE.NearestFilter);
  const color = makeFloatTarget(texSize, THREE.NearestFilter);
  const origin = makeFloatTarget(texSize, THREE.NearestFilter);

  const prev = renderer.getRenderTarget();
  renderer.setRenderTarget(posA);
  renderer.setRenderTarget(posB);
  renderer.setRenderTarget(velA);
  renderer.setRenderTarget(velB);
  renderer.setRenderTarget(meta);
  renderer.setRenderTarget(color);
  renderer.setRenderTarget(origin);
  renderer.setRenderTarget(prev);
  writeDead(posA, texSize, renderer);
  writeDead(posB, texSize, renderer);
  writeDead(velA, texSize, renderer);
  writeDead(velB, texSize, renderer);

  const dummyBase = new THREE.DataTexture(new Float32Array(4), 1, 1, THREE.RGBAFormat, THREE.FloatType);
  dummyBase.needsUpdate = true;
  dummyBase.name = 'particle-lab-base-dummy';

  const boxCenters = Array.from({ length: 16 }, () => new THREE.Vector3());
  const boxHalves = Array.from({ length: 16 }, () => new THREE.Vector3());
  let boxCount = fillBoxUniforms(boxCenters, boxHalves, boxes);

  const simUniforms = {
    tPos: { value: posA.texture as THREE.Texture },
    tVel: { value: velA.texture as THREE.Texture },
    tMeta: { value: meta.texture as THREE.Texture },
    tOrigin: { value: origin.texture as THREE.Texture },
    tHeight: { value: null as THREE.Texture | null },
    tBase: { value: null as THREE.Texture | null },
    uDt: { value: 1 / 60 },
    uBoxCenter: { value: boxCenters },
    uBoxHalf: { value: boxHalves },
    uBoxCount: { value: boxCount },
    uArenaMin: { value: new THREE.Vector2(arena.minX, arena.minZ) },
    uArenaSize: { value: new THREE.Vector2(arena.sizeX, arena.sizeZ) },
  };

  const posMat = new THREE.ShaderMaterial({
    uniforms: simUniforms,
    vertexShader: SIM_VERT,
    fragmentShader: posFragSource(),
    depthTest: false,
    depthWrite: false,
  });
  const velMat = new THREE.ShaderMaterial({
    uniforms: { ...simUniforms },
    vertexShader: SIM_VERT,
    fragmentShader: velFragSource(),
    depthTest: false,
    depthWrite: false,
  });
  const simScene = new THREE.Scene();
  const simCam = new THREE.OrthographicCamera(-1, 1, 1, -1, 0, 1);
  const simMesh = new THREE.Mesh(new THREE.PlaneGeometry(2, 2), posMat);
  simScene.add(simMesh);

  const renderUniforms = {
    tPos: { value: posA.texture as THREE.Texture },
    tVel: { value: velA.texture as THREE.Texture },
    tMeta: { value: meta.texture as THREE.Texture },
    tColor: { value: color.texture as THREE.Texture },
    tOrigin: { value: origin.texture as THREE.Texture },
    uTexSize: { value: texSize },
    uSpriteScale: { value: opts?.spriteScale ?? 1 },
    uSpriteHard: { value: opts?.spriteHard ?? 0 },
  };
  const renderMat = new THREE.ShaderMaterial({
    uniforms: renderUniforms,
    vertexShader: RENDER_VERT,
    fragmentShader: RENDER_FRAG,
    transparent: true,
    depthWrite: false,
    blending: THREE.NormalBlending,
    side: THREE.DoubleSide,
  });
  renderMat.toneMapped = false;
  const geometry = new THREE.PlaneGeometry(1, 1);
  const instanceIds = new Float32Array(capacity);
  for (let i = 0; i < capacity; i += 1) instanceIds[i] = i;
  geometry.setAttribute('aId', new THREE.InstancedBufferAttribute(instanceIds, 1));
  const mesh = new THREE.InstancedMesh(geometry, renderMat, capacity);
  mesh.frustumCulled = false;
  mesh.renderOrder = 8;
  mesh.raycast = () => {};
  mesh.count = capacity;
  mesh.instanceMatrix.setUsage(THREE.StaticDrawUsage);
  const dummy = new THREE.Object3D();
  dummy.matrix.identity();
  for (let i = 0; i < capacity; i += 1) mesh.setMatrixAt(i, dummy.matrix);
  mesh.instanceMatrix.needsUpdate = true;
  mesh.name = 'particle-lab-grains';

  let readPos = posA;
  let writePos = posB;
  let readVel = velA;
  let writeVel = velB;
  const allocator: WindowRingAllocator = createWindowRingAllocator(capacity);
  const pending: Array<{ rec: GrainSpawnRecord; slot: number }> = [];
  let disposed = false;

  const flushSpawns = (gl: THREE.WebGLRenderer) => {
    if (pending.length === 0) return;
    pending.sort((a, b) => a.slot - b.slot);
    const n = pending.length;
    const posData = new Float32Array(n * 4);
    const velData = new Float32Array(n * 4);
    const metaData = new Float32Array(n * 4);
    const colorData = new Float32Array(n * 4);
    const originData = new Float32Array(n * 4);
    const slots = new Array<number>(n);
    for (let i = 0; i < n; i += 1) {
      const p = pending[i].rec;
      slots[i] = pending[i].slot;
      posData[i * 4] = p.x;
      posData[i * 4 + 1] = p.y;
      posData[i * 4 + 2] = p.z;
      posData[i * 4 + 3] = 0;
      velData[i * 4] = p.vx;
      velData[i * 4 + 1] = p.vy;
      velData[i * 4 + 2] = p.vz;
      velData[i * 4 + 3] = p.drag;
      metaData[i * 4] = p.radius;
      metaData[i * 4 + 1] = p.restitution;
      metaData[i * 4 + 2] = p.friction;
      metaData[i * 4 + 3] = p.materialId;
      colorData[i * 4] = p.r;
      colorData[i * 4 + 1] = p.g;
      colorData[i * 4 + 2] = p.b;
      colorData[i * 4 + 3] = p.spin;
      originData[i * 4] = p.ox;
      originData[i * 4 + 1] = p.oy;
      originData[i * 4 + 2] = p.oz;
      originData[i * 4 + 3] = p.maxLife;
    }
    const writeRun = (offset: number, start: number, count: number) => {
      const spans = grainRowSpans(start, count, texSize);
      const slice4 = (src: Float32Array) => src.subarray(offset * 4, (offset + count) * 4);
      uploadSpans(gl, readPos, slice4(posData), spans);
      uploadSpans(gl, writePos, slice4(posData), spans);
      uploadSpans(gl, readVel, slice4(velData), spans);
      uploadSpans(gl, writeVel, slice4(velData), spans);
      uploadSpans(gl, meta, slice4(metaData), spans);
      uploadSpans(gl, color, slice4(colorData), spans);
      uploadSpans(gl, origin, slice4(originData), spans);
    };
    const runs = slotRuns(slots);
    for (const run of runs) writeRun(run.offset, run.start, run.count);
    pending.length = 0;
  };

  const bindSim = (heightTex: THREE.Texture, baseTex: THREE.Texture | null | undefined, dt: number) => {
    simUniforms.tPos.value = readPos.texture;
    simUniforms.tVel.value = readVel.texture;
    simUniforms.tMeta.value = meta.texture;
    simUniforms.tOrigin.value = origin.texture;
    simUniforms.tHeight.value = heightTex;
    simUniforms.tBase.value = baseTex ?? dummyBase;
    simUniforms.uDt.value = dt;
    simUniforms.uBoxCount.value = boxCount;
    velMat.uniforms.tPos.value = readPos.texture;
    velMat.uniforms.tVel.value = readVel.texture;
    velMat.uniforms.tMeta.value = meta.texture;
    velMat.uniforms.tOrigin.value = origin.texture;
    velMat.uniforms.tHeight.value = heightTex;
    velMat.uniforms.tBase.value = baseTex ?? dummyBase;
    velMat.uniforms.uDt.value = dt;
    velMat.uniforms.uBoxCount.value = boxCount;
  };

  return {
    capacity,
    texSize,
    mesh,
    posTexture: () => readPos.texture,
    velTexture: () => readVel.texture,
    metaTexture: () => meta.texture,
    originTexture: () => origin.texture,
    enqueue(records, simTime = 0) {
      const lives = new Array<number>(records.length);
      for (let i = 0; i < records.length; i += 1) lives[i] = records[i].maxLife;
      const slots = allocator.allocateWithLives(lives, simTime);
      for (let i = 0; i < slots.length; i += 1) {
        pending.push({ rec: records[i], slot: slots[i] });
      }
      return slots.length;
    },
    pendingCount() {
      return pending.length;
    },
    unexpiredCount(now) {
      return allocator.unexpiredCount(now);
    },
    step(gl, dt, heightTex, baseTex, _liveCount = 0) {
      if (disposed) return;
      const clamped = Math.min(Math.max(dt, 0), 0.05);
      bindSim(heightTex, baseTex, clamped);
      const prevTarget = gl.getRenderTarget();
      const prevAuto = gl.autoClear;
      gl.autoClear = true;
      simMesh.material = posMat;
      gl.setRenderTarget(writePos);
      gl.render(simScene, simCam);
      simMesh.material = velMat;
      gl.setRenderTarget(writeVel);
      gl.render(simScene, simCam);
      gl.setRenderTarget(prevTarget);
      gl.autoClear = prevAuto;
      const tmpPos = readPos;
      readPos = writePos;
      writePos = tmpPos;
      const tmpVel = readVel;
      readVel = writeVel;
      writeVel = tmpVel;
      flushSpawns(gl);
      allocator.beginStep();
      renderUniforms.tPos.value = readPos.texture;
      renderUniforms.tVel.value = readVel.texture;
      renderUniforms.tOrigin.value = origin.texture;
    },
    reset(gl) {
      pending.length = 0;
      allocator.reset();
      writeDead(posA, texSize, gl);
      writeDead(posB, texSize, gl);
      writeDead(velA, texSize, gl);
      writeDead(velB, texSize, gl);
    },
    setColliders(next) {
      boxCount = fillBoxUniforms(boxCenters, boxHalves, next);
    },
    dispose() {
      if (disposed) return;
      disposed = true;
      posA.dispose();
      posB.dispose();
      velA.dispose();
      velB.dispose();
      meta.dispose();
      color.dispose();
      origin.dispose();
      posMat.dispose();
      velMat.dispose();
      renderMat.dispose();
      geometry.dispose();
      simMesh.geometry.dispose();
      dummyBase.dispose();
    },
  };
}
